import { JobSectionVerb } from './job.section.rights';
import { MobileTechniciansSecurityRight } from './mobile.technicians.security.module';

export type FieldAppSection = 'tasks' | 'items' | 'sors' | 'files' | 'assets' | 'defects' | 'invoices' | 'equipment';

const R = MobileTechniciansSecurityRight;

/**
 * The Field App's job-tab rights. Each View is the "Manage Job: View …" right the Field App already reads to show
 * the tab, so an existing role keeps its tabs on every app version; SORs get their own View instead of following
 * View Items. A row holds only the actions its tab offers.
 */
export const FieldAppSectionRightIds: Record<FieldAppSection, Partial<Record<JobSectionVerb, MobileTechniciansSecurityRight>>> = {
    tasks: { view: R.ManageJobViewTasks, edit: R.TasksEdit },
    items: { view: R.ManageJobViewItems, create: R.ItemsCreate, edit: R.ItemsEdit, delete: R.ItemsDelete },
    sors: { view: R.SorsView, create: R.SorsCreate, edit: R.SorsEdit, delete: R.SorsDelete },
    files: { view: R.ManageJobViewFiles },
    assets: { view: R.ManageJobViewAssets, create: R.AssetsCreate, edit: R.AssetsEdit, delete: R.AssetsDelete },
    defects: { view: R.ManageJobViewDefects },
    invoices: { view: R.ManageJobViewInvoices, create: R.InvoicesCreate, edit: R.InvoicesEdit },
    equipment: { view: R.ManageJobViewEquipments, create: R.EquipmentCreate, edit: R.EquipmentEdit, delete: R.EquipmentDelete },
};

/** Replaced by the Assets / Equipment rows' Create, Edit and Delete; shown as deprecated until the Field App checks those. */
export const FIELD_APP_DEPRECATED_TAB_RIGHTS: MobileTechniciansSecurityRight[] = [R.ManageJobManageAssets, R.ManageJobManageEquipments];

/** The actions each row offers, in the order of the Sections / Tabs columns. */
export function fieldAppSectionVerbs(section: FieldAppSection): JobSectionVerb[] {

    const ids = FieldAppSectionRightIds[section];

    return (['view', 'create', 'edit', 'delete'] as JobSectionVerb[]).filter(verb => ids[verb] !== undefined);
}

/** The rights WBS-11925 added; the View rights of the rows existed before and are not in this list. */
export const FIELD_APP_NEW_SECTION_RIGHTS: MobileTechniciansSecurityRight[] = [
    R.TasksEdit, R.ItemsCreate, R.ItemsEdit, R.ItemsDelete, R.SorsView, R.SorsCreate, R.SorsEdit, R.SorsDelete,
    R.AssetsCreate, R.AssetsEdit, R.AssetsDelete, R.InvoicesCreate, R.InvoicesEdit, R.EquipmentCreate, R.EquipmentEdit, R.EquipmentDelete,
];

/**
 * Field App rights no system role may hold and release day never grants; a tenant grants them only on a custom role.
 * Kept apart from FIELD_APP_NEW_SECTION_RIGHTS because some of those WBS-11925 rights return on release day.
 */
const FIELD_APP_OFF_BY_DEFAULT_RIGHTS: MobileTechniciansSecurityRight[] = [R.FieldAppBulkActions];

/**
 * The new Field App rights a role needs on release day so technicians keep doing what they do today: View Tasks
 * could edit tasks, View Items could add, edit and delete items, Manage Assets / Equipment managed that tab.
 * Manage Assets / Equipment never add the tab's View: that View is the id that shows the tab in the Field App, so
 * adding it would open a tab the role never had. SORs and Invoices get nothing — they are granted deliberately.
 * Applied once per role (system roles in their class, custom roles by the tenant upgrade), never re-derived.
 */
export function fieldAppReleaseRights(rights: number[]): MobileTechniciansSecurityRight[] {

    const has = (right: number) => rights.includes(right);

    return [
        ...(has(R.ManageJobViewTasks) ? [R.TasksEdit] : []),
        ...(has(R.ManageJobViewItems) ? [R.ItemsCreate, R.ItemsEdit, R.ItemsDelete] : []),
        ...(has(R.ManageJobManageAssets) ? [R.AssetsCreate, R.AssetsEdit, R.AssetsDelete] : []),
        ...(has(R.ManageJobManageEquipments) ? [R.EquipmentCreate, R.EquipmentEdit, R.EquipmentDelete] : []),
    ];
}

/**
 * A system role's rights with the new Field App rights replaced by the release-day ones and the off-by-default rights
 * removed, so a whole-module spread cannot grant more.
 */
export function withFieldAppReleaseRights(rights: number[]): number[] {

    const isWithheld = (right: number) => FIELD_APP_NEW_SECTION_RIGHTS.includes(right) || FIELD_APP_OFF_BY_DEFAULT_RIGHTS.includes(right);
    const existing = rights.filter(right => !isWithheld(right));

    return [...new Set([...existing, ...fieldAppReleaseRights(existing)])];
}
